import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { trackPurchase } from "@/lib/analytics-enhanced";
import { takePendingPurchase, toMajorUnits } from "@/lib/purchase-attribution";
import { pendingSearchAwaitsCheckout, RESUME_PARAM } from "@/lib/pending-statewide-search";
import { GuestCheckoutDialog } from "@/components/pricing/GuestCheckoutDialog";

/** Poll for ~30s. The webhook usually lands in a second or two, but a slow one
 *  used to mean the user saw "Payment successful" next to a UI still saying Free. */
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 15;

const PAID_NOT_YET =
  "Payment received, but your account hasn't updated yet. It usually lands within a few minutes — email cole@wisconsinpropertymap.com if it doesn't.";

// Signed in, but this account has no Lite. Most likely it isn't the address
// they paid with (Google with a different email); otherwise the webhook is late.
const SIGNED_IN_NOT_YET =
  "You're signed in, but this account doesn't have Lite yet. If you paid with a different email, sign in with that one. Otherwise it usually lands within a few minutes — email cole@wisconsinpropertymap.com if it doesn't.";

/**
 * Handles the `?checkout=` parameters Stripe redirects back with, and the
 * `?checkout=signed_in` a guest checkout's emailed link returns to.
 *
 * On success it polls `checkSubscription` until entitlement actually flips,
 * rather than firing once and hoping. The old single 2s delay raced the webhook
 * and lost often enough to generate "I paid and nothing happened" email — and
 * the poll is also the safety net for the webhook being late or retried.
 */
export function CheckoutHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkSubscription, isPremium, user, loading } = useAuthContext();
  const hasHandled = useRef(false);
  // Paid without signing in: the account is waiting behind an emailed link.
  const [guestReturn, setGuestReturn] = useState(false);
  const cancelled = useRef(false);

  // Read through a ref so the polling effect doesn't restart on every change.
  const isPremiumRef = useRef(isPremium);
  isPremiumRef.current = isPremium;

  useEffect(() => () => { cancelled.current = true; }, []);

  /** Poll until Lite shows on the signed-in account, then say so — or say plainly that it hasn't. */
  const awaitLite = useCallback(
    async (toastId: string | number, notYet: string) => {
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (cancelled.current) return;

        await checkSubscription();
        if (cancelled.current) return;

        if (isPremiumRef.current) {
          // Bought from the daily cap: the search it refused runs now, rather
          // than leaving the buyer on the home page to type it again.
          if (pendingSearchAwaitsCheckout()) {
            toast.success("You're on Lite. Running your search…", { id: toastId, duration: 6000 });
            navigate(`/map?${RESUME_PARAM}=1`);
            return;
          }
          toast.success("You're on Lite. CSV export and the full filters are unlocked.", {
            id: toastId,
            duration: 6000,
          });
          return;
        }
      }

      // Payment succeeded, but entitlement hasn't landed. Say so plainly
      // instead of leaving a spinner up: the webhook may still be retrying,
      // and support needs the signal.
      toast.warning(notYet, { id: toastId, duration: 12000 });
    },
    [checkSubscription, navigate],
  );

  useEffect(() => {
    const checkoutStatus = searchParams.get("checkout");
    if (!checkoutStatus || hasHandled.current) return;
    // Every branch turns on who is signed in, and after a full-page return
    // (from Stripe, or from an emailed link) that isn't known until the stored
    // or linked session has been restored. Deciding before then read every
    // signed-in buyer as signed out: they were told to sign in, and nothing
    // waited for their Lite.
    if (loading) return;
    hasHandled.current = true;

    if (checkoutStatus === "success") {
      // Report at the redirect, not at the entitlement flip: Stripe only sends
      // us here after a successful payment, and the poll below can legitimately
      // time out on a slow webhook — a purchase that happened must not go
      // uncounted just because activation was late. Read-and-clear, so a
      // back-navigation can't fire it twice.
      const pending = takePendingPurchase();
      trackPurchase({
        transactionId: pending?.sessionId,
        value: toMajorUnits(pending?.amountTotal),
        currency: pending?.currency ?? undefined,
        upgradeEntry: pending?.upgradeEntry,
      });

      if (user) {
        void awaitLite(toast.loading("Payment received — activating your account…"), PAID_NOT_YET);
      } else if (searchParams.get("guest") === "1") {
        // No session in this browser to poll: the webhook put Lite on the
        // account for the email they paid with, and the dialog says how to
        // sign in to it.
        setGuestReturn(true);
      } else {
        toast.success("Payment successful. Sign in to use your Lite features.");
      }
    } else if (checkoutStatus === "signed_in") {
      // Back from the link a guest checkout emailed, or from Google. With no
      // session the link failed (expired, or already used) and
      // AuthLinkErrorHandler says so; the param is cleared either way.
      if (user) void awaitLite(toast.loading("Signed in — checking for Lite…"), SIGNED_IN_NOT_YET);
    } else if (checkoutStatus === "cancelled") {
      toast.info("Checkout canceled — you haven't been charged.");
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("checkout");
    newParams.delete("guest");
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, user, loading, awaitLite]);

  // Opening the emailed link in another tab signs this one in too. Once it
  // has, the dialog's "sign in with your email" is out of date.
  useEffect(() => {
    if (!guestReturn || !user) return;
    setGuestReturn(false);
    void awaitLite(toast.loading("Signed in — checking for Lite…"), SIGNED_IN_NOT_YET);
  }, [guestReturn, user, awaitLite]);

  return <GuestCheckoutDialog open={guestReturn} onOpenChange={setGuestReturn} />;
}
